// src/api/apiHelpers.tsx
import { AxiosError } from "axios";
import { ApiError, Environment } from "types";
import logger from "utils/logger";
import api from "./api";

// ============================================================================
// Types
// ============================================================================
interface ApiErrorResponse {
  detail?: string;
  error?: string;
  message?: string;
  [key: string]: unknown;
}

// ============================================================================
// Error Handling Utilities
// ============================================================================
const extractErrorMessage = (error: unknown): string => {
  if (error instanceof AxiosError) {
    const data = error.response?.data as ApiErrorResponse | undefined;
    if (data?.detail) return data.detail;
    if (data?.error) return data.error;
    if (data?.message) return data.message;
    if (error.message) return error.message;
  }
  if (error instanceof Error) return error.message;
  return "An unexpected error occurred";
};

const logError = (context: string, endpoint: string, error: unknown) => {
  const message = extractErrorMessage(error);
  // In production, replace with proper logging service (e.g., Sentry)
  if (process.env.NODE_ENV === "development") {
    logger.error(`[API] ${context} ${endpoint}:`, message);
  }
};

// ============================================================================
// API Helper Functions
// ============================================================================

/**
 * Fetch data from an endpoint
 * @throws Error with user-friendly message
 */
export const fetchData = async <T,>(endpoint: string): Promise<T> => {
  try {
    const response = await api.get<T>(endpoint);
    return response.data;
  } catch (error) {
    logError("GET", endpoint, error);
    throw new Error(extractErrorMessage(error));
  }
};

/**
 * Create a new resource (POST)
 * @throws Original error for caller to handle specific status codes
 */
export const createData = async <T, R,>(endpoint: string, data: T): Promise<R> => {
  try {
    const response = await api.post<R>(endpoint, data);
    return response.data;
  } catch (error) {
    logError("POST", endpoint, error);
    // Rethrow original error so caller can handle specific cases (e.g., validation errors)
    throw error;
  }
};

/**
 * Upload file(s) to an endpoint
 * @returns Response data (type-safe when caller specifies)
 */
export const uploadData = async <R = unknown,>(
  endpoint: string,
  data: FormData | object,
  contentType: "multipart/form-data" | "application/json" = "multipart/form-data"
): Promise<R> => {
  try {
    const response = await api.post<R>(endpoint, data, {
      headers: {
        "Content-Type": contentType,
      },
    });
    return response.data;
  } catch (error) {
    logError("UPLOAD", endpoint, error);
    throw new Error(extractErrorMessage(error));
  }
};

/**
 * Update a resource (PATCH)
 */
export const updateData = async <T, R,>(endpoint: string, data: T): Promise<R> => {
  try {
    const response = await api.patch<R>(endpoint, data, {
      headers: {
        "Content-Type": "application/json",
      },
    });
    return response.data;
  } catch (error) {
    logError("PATCH", endpoint, error);
    throw new Error(extractErrorMessage(error));
  }
};

/**
 * Delete a resource
 * Gracefully handles 404 (already deleted)
 */
export const deleteData = async (endpoint: string): Promise<void> => {
  try {
    await api.delete(endpoint);
    logger.info(`[API] DELETE ${endpoint}: success`);
  } catch (error) {
    const axiosError = error as AxiosError;
    const status = axiosError.response?.status;

    // 404 = already deleted, treat as success
    if (status === 404) {
      logger.info(`[API] DELETE ${endpoint}: already deleted (404)`);
      return;
    }

    logError("DELETE", endpoint, error);
    throw new Error(extractErrorMessage(error));
  }
};

/**
 * Delete a resource and return the response body (e.g. an async task handle).
 * Unlike deleteData, surfaces the JSON payload and does not swallow 404.
 */
export const deleteDataWithBody = async <R,>(endpoint: string): Promise<R> => {
  try {
    const response = await api.delete<R>(endpoint);
    return response.data;
  } catch (error) {
    logError("DELETE", endpoint, error);
    throw error;
  }
};

/**
 * Fetch available environments
 */
export const fetchEnvironments = async <T = Environment[],>(): Promise<T> => {
  try {
    const response = await api.get<T>("/environments/");
    return response.data;
  } catch (error) {
    logError("GET", "/environments/", error);
    throw new Error(extractErrorMessage(error));
  }
};

// Re-export types for convenience
export type { ApiError };
